import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getOppSpreadsTop,
  type OppSpreadItem,
  type OppSpreadTopSortBy,
} from '@/services/oppDetectorApi';
import { categoryBadgeClass, hasCategory, normalizeCategory, renderCategoryLabel } from '@/lib/instrumentCategory';
import './SpreadTopList.css';

const DEFAULT_LIMIT = 100;
const DEFAULT_WINDOW_SECONDS = 60;
const POLL_MS = 10000;

const SORT_OPTIONS: Array<{ value: OppSpreadTopSortBy; label: string }> = [
  { value: 'spread', label: '价差' },
  { value: 'abs_spread', label: '|价差|' },
  { value: 'relative_open_line', label: '相对 open' },
];

function formatSpreadPct(spread: number): string {
  if (!Number.isFinite(spread)) return '—';
  return `${(spread * 100).toFixed(4)}%`;
}

function formatRatio(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${ratio.toFixed(2)}x`;
}

function shortExchange(exchange: string): string {
  const map: Record<string, string> = {
    OKX: 'OKX',
    Bybit: 'BB',
    Binance: 'BN',
    Gateio: 'GT',
    Bitget: 'BG',
    Hyperliquid: 'HL',
    Lighter: 'LT',
    Aster: 'AS',
    EdgeX: 'EX',
    Paradex: 'PD',
  };
  return map[exchange] ?? exchange.slice(0, 4);
}

type Props = {
  exchange1: string;
  exchange2: string;
  symbolFilter?: string;
  enabled: boolean;
  selectedSymbol?: string;
  selectedBuyExchange?: string;
  selectedSellExchange?: string;
  onPick: (item: OppSpreadItem) => void;
};

export function SpreadTopList({
  exchange1,
  exchange2,
  symbolFilter = '',
  enabled,
  selectedSymbol,
  selectedBuyExchange,
  selectedSellExchange,
  onPick,
}: Props) {
  const [rows, setRows] = useState<OppSpreadItem[]>([]);
  const [sortBy, setSortBy] = useState<OppSpreadTopSortBy>('spread');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const mounted = useRef(true);

  const exchangesReady =
    Boolean(exchange1.trim()) && Boolean(exchange2.trim()) && exchange1.trim() !== exchange2.trim();

  const apiExchanges = useMemo(
    () => (exchangesReady ? [exchange1.trim(), exchange2.trim()] : []),
    [exchange1, exchange2, exchangesReady],
  );

  const load = useCallback(async () => {
    if (!enabled || !exchangesReady) return;
    try {
      const data = await getOppSpreadsTop(DEFAULT_LIMIT, DEFAULT_WINDOW_SECONDS, sortBy, apiExchanges);
      if (!mounted.current) return;
      setRows(data);
      setError(null);
      setLastOkAt(Date.now());
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [apiExchanges, enabled, exchangesReady, sortBy]);

  useEffect(() => {
    mounted.current = true;
    if (!enabled || !exchangesReady) {
      setRows([]);
      setLoading(false);
      return () => {
        mounted.current = false;
      };
    }
    setLoading(true);
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
    };
  }, [enabled, exchangesReady, load]);

  const filteredRows = useMemo(() => {
    const needle = symbolFilter.trim().toUpperCase();
    if (!needle) return rows;
    return rows.filter((item) => item.symbol.toUpperCase().includes(needle));
  }, [rows, symbolFilter]);

  const categoryOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((item) => normalizeCategory(item.category)).filter((type) => type.length > 0))).sort(
        (a, b) => a.localeCompare(b),
      ),
    [rows],
  );

  const [localCategoryFilter, setLocalCategoryFilter] = useState('');

  const displayRows = useMemo(() => {
    const tag = localCategoryFilter.trim();
    if (!tag) return filteredRows;
    return filteredRows.filter((item) => normalizeCategory(item.category) === tag);
  }, [localCategoryFilter, filteredRows]);

  useEffect(() => {
    if (!localCategoryFilter || categoryOptions.includes(localCategoryFilter)) return;
    setLocalCategoryFilter('');
  }, [categoryOptions, localCategoryFilter]);

  if (!exchangesReady) {
    return <p className="spread-top-list-hint">请选择两个不同交易所以加载 Top {DEFAULT_LIMIT} 价差。</p>;
  }

  return (
    <section className="spread-top-list" aria-label={`价差 Top ${DEFAULT_LIMIT}`}>
      <div className="spread-top-list-toolbar">
        <span className="spread-top-list-title">Top {DEFAULT_LIMIT}</span>
        <label className="spread-top-list-sort">
          <span className="visually-hidden">排序</span>
          <select value={sortBy} onChange={(event) => setSortBy(event.target.value as OppSpreadTopSortBy)}>
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {categoryOptions.length > 0 ? (
          <label className="spread-top-list-category">
            <span className="visually-hidden">类别</span>
            <select value={localCategoryFilter} onChange={(event) => setLocalCategoryFilter(event.target.value)}>
              <option value="">全部类别</option>
              {categoryOptions.map((type) => (
                <option key={type} value={type}>
                  {renderCategoryLabel(type)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <button type="button" className="spread-top-list-refresh" disabled={loading} onClick={() => void load()}>
          刷新
        </button>
      </div>

      <p className="spread-top-list-meta">
        {loading && rows.length === 0 ? '加载中…' : `展示 ${displayRows.length} / ${rows.length}`}
        {lastOkAt != null ? ` · ${new Date(lastOkAt).toLocaleTimeString('zh-CN', { hour12: false })}` : ''}
      </p>

      {error ? <div className="spread-top-list-error">{error}</div> : null}

      <ul className="spread-top-list-rows" role="listbox" aria-label="价差排行">
        {!loading && displayRows.length === 0 && !error ? (
          <li className="spread-top-list-hint">暂无匹配价差</li>
        ) : null}
        {displayRows.map((item, index) => {
          const isSelected =
            item.symbol === selectedSymbol &&
            toUiExchange(item.buy_exchange).toLowerCase() === selectedBuyExchange?.toLowerCase() &&
            toUiExchange(item.sell_exchange).toLowerCase() === selectedSellExchange?.toLowerCase();
          return (
            <li key={`${item.symbol}-${item.buy_exchange}-${item.sell_exchange}-${index}`}>
              <button
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`spread-top-list-row${isSelected ? ' is-selected' : ''}`}
                onClick={() => onPick(item)}
              >
                <span className="spread-top-list-row-rank">{index + 1}</span>
                <span className="spread-top-list-row-main">
                  <span className="spread-top-list-row-head">
                    <span className="spread-top-list-row-symbol">{item.symbol}</span>
                    {hasCategory(item.category) ? (
                      <span className={`spread-top-list-row-category ${categoryBadgeClass(item.category)}`}>
                        {renderCategoryLabel(item.category)}
                      </span>
                    ) : null}
                  </span>
                  <span className="spread-top-list-row-exchanges">
                    {shortExchange(item.buy_exchange)} → {shortExchange(item.sell_exchange)}
                  </span>
                </span>
                <span className="spread-top-list-row-metrics">
                  <span className="spread-top-list-row-spread" title="Spread">
                    {formatSpreadPct(item.spread)}
                  </span>
                  <span className="spread-top-list-row-ratio" title="相对 open line">
                    {formatRatio(item.open_line_ratio)}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function oppSpreadItemToSelectorValue(item: OppSpreadItem): {
  symbol: string;
  exchange1: string;
  exchange2: string;
  instId1: string;
  instId2: string;
  direction: 'long1_short2';
} {
  return {
    symbol: item.symbol,
    exchange1: toUiExchange(item.buy_exchange),
    exchange2: toUiExchange(item.sell_exchange),
    instId1: item.buy_inst_id,
    instId2: item.sell_inst_id,
    direction: 'long1_short2',
  };
}

export function toUiExchange(exchange: string): string {
  const normalized = exchange.trim().toLowerCase();
  if (normalized === 'okx') return 'OKX';
  if (normalized === 'bybit') return 'Bybit';
  if (normalized === 'binance') return 'Binance';
  if (normalized === 'bitget') return 'Bitget';
  if (normalized === 'gateio') return 'Gateio';
  if (normalized === 'hyperliquid') return 'Hyperliquid';
  if (normalized === 'aster') return 'Aster';
  if (normalized === 'edgex') return 'EdgeX';
  if (normalized === 'lighter') return 'Lighter';
  if (normalized === 'paradex') return 'Paradex';
  return exchange;
}
